<script lang="ts">
  import { Copy } from 'lucide-svelte';
  import { selectEntity } from '$lib/router';
  import { SCENE_TEMPLATES } from '$lib/constants/scene';
  import { LOAD_TYPES } from '$lib/constants/load';
  import { scenesQuery } from '$lib/queries/scenes';
  import { sceneLoadsQuery } from '$lib/queries/scene_loads';
  import { loadsQuery } from '$lib/queries/loads';
  import { roomsQuery } from '$lib/queries/rooms';
  import { zonesQuery } from '$lib/queries/zones';
  import { createSceneLoadMutation, updateSceneLoadMutation } from '$lib/mutations/scene_loads';
  import { cloneScene } from '$lib/api';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';
  import type { SceneLoadResponse } from '$lib/types/scene_load';
  import type { SceneResponse } from '$lib/types/scene';
  import type { LoadResponse } from '$lib/types/load';
  import type { RoomResponse } from '$lib/types/room';

  let { id }: { id: number } = $props();

  let selectedTemplate = $state('');
  const unitScenes = scenesQuery();
  const unitSceneLoads = sceneLoadsQuery(() => id);
  const unitLoads = loadsQuery();
  const unitRooms = roomsQuery();
  const unitZones = zonesQuery();
  const createSceneLoad = createSceneLoadMutation(() => id);
  const updateSceneLoad = updateSceneLoadMutation(() => id);

  const scene = $derived((unitScenes.data ?? []).find((s) => s.id === id));
  const inScopeLoads = $derived(
    getInScopeLoads(scene, unitLoads.data ?? [], unitRooms.data ?? [])
  );
  const sceneLoadMap = $derived(buildSceneLoadMap(unitSceneLoads.data ?? []));
  const saving = $derived(createSceneLoad.isPending || updateSceneLoad.isPending);

  function getInScopeLoads(
    sceneEntity: SceneResponse | undefined,
    allLoads: LoadResponse[],
    allRooms: RoomResponse[]
  ) {
    if (!sceneEntity) return [];

    switch (sceneEntity.scope) {
      case 'room':
        return allLoads.filter((l) => l.room === sceneEntity.scope_id);
      case 'zone': {
        const zoneRoomIds = new Set(
          allRooms.filter((r) => r.zone === sceneEntity.scope_id).map((r) => r.id)
        );
        return allLoads.filter((l) => zoneRoomIds.has(l.room));
      }
      case 'unit':
        return allLoads;
      default:
        return allLoads;
    }
  }

  function buildSceneLoadMap(allSceneLoads: Array<SceneLoadResponse>) {
    const map = new Map<number, SceneLoadResponse>();
    for (const sl of allSceneLoads) {
      map.set(sl.load_id, sl);
    }
    return map;
  }

  function normalizedValue(sceneLoad: SceneLoadResponse | undefined) {
    if (!sceneLoad || sceneLoad.value == null) return 0;
    return sceneLoad.value / 100;
  }

  function displayPercent(sceneLoad: SceneLoadResponse | undefined) {
    return Math.round(normalizedValue(sceneLoad) * 100);
  }

  function roomName(load: LoadResponse) {
    const room = (unitRooms.data ?? []).find((r) => r.id === load.room);
    return room?.name ?? '—';
  }

  async function updateLoadValue(load: LoadResponse, rawValue: number) {
    const value = Math.round(Math.max(0, Math.min(1, rawValue)) * 100);
    const existing = sceneLoadMap.get(load.id);

    try {
      if (existing) {
        await updateSceneLoad.mutateAsync({ id: existing.id, request: { value } });
      } else {
        await createSceneLoad.mutateAsync({
          scene: id,
          load: load.id,
          value,
          fade_ms: 2000,
          delay_ms: 0
        });
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  async function applyTemplate() {
    if (!selectedTemplate || !SCENE_TEMPLATES[selectedTemplate]) return;

    const template = SCENE_TEMPLATES[selectedTemplate];

    try {
      for (const load of inScopeLoads) {
        const value = Math.round(template.getLoadValue(load) * 100);
        const existing = sceneLoadMap.get(load.id);

        if (existing) {
          await updateSceneLoad.mutateAsync({ id: existing.id, request: { value } });
        } else {
          await createSceneLoad.mutateAsync({
            scene: id,
            load: load.id,
            value,
            fade_ms: 2000,
            delay_ms: 0
          });
        }
      }
      toast.success(`Applied "${template.name}" template`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  async function duplicateScene() {
    try {
      const cloned = await cloneScene(id);
      toast.success(`Duplicated as "${cloned.name}"`);
      selectEntity('scene', cloned.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  function isDimmer(loadType: string) {
    return ['phase_cut', '1-10v', 'dali-dt6', 'dali-dt8'].includes(loadType);
  }
</script>

<div class="p-3 space-y-3">
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div>
      <h2 class="text-base font-medium text-text-primary">{scene?.name ?? `Scene ${id}`}</h2>
      {#if scene?.scope}
        <p class="text-xs text-text-muted capitalize">
          Scope: {scene.scope}
          {#if scene.scope_id != null}
            · <span class="font-mono">{scene.scope_id}</span>
          {/if}
        </p>
      {/if}
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <select
        bind:value={selectedTemplate}
        class="h-8 px-2 text-sm border border-border-strong rounded bg-bg-canvas"
      >
        <option value="">Apply template…</option>
        {#each Object.entries(SCENE_TEMPLATES) as [key, template]}
          <option value={key}>{template.name}</option>
        {/each}
      </select>
      <button
        type="button"
        class="h-8 px-3 text-sm border border-border-default rounded hover:bg-bg-hover disabled:opacity-50"
        disabled={!selectedTemplate || saving}
        onclick={applyTemplate}
      >
        Apply
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-1.5 h-8 px-3 text-sm border border-border-default rounded hover:bg-bg-hover"
        onclick={duplicateScene}
      >
        <Copy class="w-3.5 h-3.5" />
        Duplicate
      </button>
    </div>
  </div>

  {#if inScopeLoads.length === 0}
    <p class="text-sm text-text-muted py-4">No loads in scope for this scene.</p>
  {:else}
    <div class="border border-border-default rounded overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-bg-rail text-text-muted text-xs">
          <tr class="h-8">
            <th class="text-left px-3 font-medium">Load</th>
            <th class="text-left px-3 font-medium w-28">Room</th>
            <th class="text-left px-3 font-medium w-32">Type</th>
            <th class="text-left px-3 font-medium">Value</th>
            <th class="text-right px-3 font-medium w-16">%</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-border-default">
          {#each inScopeLoads as load}
            {@const sl = sceneLoadMap.get(load.id)}
            {@const pct = displayPercent(sl)}
            <tr class="h-8">
              <td class="px-3 font-medium">{load.name}</td>
              <td class="px-3 text-text-secondary text-xs">{roomName(load)}</td>
              <td class="px-3 text-text-secondary text-xs">
                {LOAD_TYPES[load.load_type as keyof typeof LOAD_TYPES]?.label ?? load.load_type}
              </td>
              <td class="px-3">
                {#if isDimmer(load.load_type)}
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={normalizedValue(sl)}
                    disabled={saving}
                    class="w-full max-w-xs"
                    oninput={(e) => updateLoadValue(load, parseFloat(e.currentTarget.value))}
                  />
                {:else if load.load_type === 'curtain'}
                  <div class="inline-flex gap-1">
                    <button
                      type="button"
                      class="px-2 py-0.5 text-xs rounded border {pct === 0 ? 'bg-bg-selected border-accent-primary' : 'border-border-default'}"
                      disabled={saving}
                      onclick={() => updateLoadValue(load, 0)}
                    >
                      Open
                    </button>
                    <button
                      type="button"
                      class="px-2 py-0.5 text-xs rounded border {pct === 100 ? 'bg-bg-selected border-accent-primary' : 'border-border-default'}"
                      disabled={saving}
                      onclick={() => updateLoadValue(load, 1)}
                    >
                      Close
                    </button>
                  </div>
                {:else}
                  <div class="inline-flex gap-1">
                    <button
                      type="button"
                      class="px-2 py-0.5 text-xs rounded border {pct === 0 ? 'bg-bg-selected border-accent-primary' : 'border-border-default'}"
                      disabled={saving}
                      onclick={() => updateLoadValue(load, 0)}
                    >
                      Off
                    </button>
                    <button
                      type="button"
                      class="px-2 py-0.5 text-xs rounded border {pct === 100 ? 'bg-bg-selected border-accent-primary' : 'border-border-default'}"
                      disabled={saving}
                      onclick={() => updateLoadValue(load, 1)}
                    >
                      On
                    </button>
                  </div>
                {/if}
              </td>
              <td class="px-3 text-right font-mono text-text-secondary">{pct}%</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>
